import React from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@tracify/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@tracify/ui/tabs";
import Spinner from "@tracify/ui/spinner";

import DiscountCodesAllPerformanceOverview from "./DiscountCodesAllPerformanceOverview";
import { useDiscountCodesAll } from "@lib/api-hooks/useDiscountCodesAll";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { useNewVsReturningState } from "@lib/hooks/url-state/use-filter-state";

const PAGE_SIZE = 10;

function DiscountCodesAllWithStoreTabs() {
  const { selectedStores } = useSelectedStores();
  const [paginationByStore, setPaginationByStore] = React.useState<
    Record<string, { pageIndex: number; pageSize: number }>
  >({});

  if (!selectedStores?.length) {
    return null;
  }

  const getPagination = (siteId: string) =>
    paginationByStore[siteId] ?? { pageIndex: 0, pageSize: PAGE_SIZE };
  const setPagination =
    (siteId: string) =>
    (
      updaterOrValue:
        | { pageIndex: number; pageSize: number }
        | ((prev: { pageIndex: number; pageSize: number }) => {
            pageIndex: number;
            pageSize: number;
          })
    ) => {
      setPaginationByStore((prev) => ({
        ...prev,
        [siteId]:
          typeof updaterOrValue === "function"
            ? updaterOrValue(prev[siteId] ?? { pageIndex: 0, pageSize: 10 })
            : updaterOrValue,
      }));
    };

  if (selectedStores.length === 1) {
    const store = selectedStores[0];
    return (
      <DiscountCodesAllPerformanceOverview
        siteId={store.csid}
        pagination={getPagination(store.csid)}
        onPaginationChange={setPagination(store.csid)}
      />
    );
  }

  return (
    <Tabs defaultValue={selectedStores[0]?.csid} className="overflow-visible">
      <TabsList className="mt-1 w-full lg:w-fit">
        {selectedStores.map((store) => (
          <TabsTrigger key={store.csid} value={store.csid} className="flex-1">
            {store.description || store.name}
          </TabsTrigger>
        ))}
      </TabsList>
      {selectedStores.map((store) => (
        <TabsContent
          key={store.csid}
          value={store.csid}
          className="overflow-visible pt-1"
        >
          <DiscountCodesAllPerformanceOverview
            siteId={store.csid}
            pagination={getPagination(store.csid)}
            onPaginationChange={setPagination(store.csid)}
          />
        </TabsContent>
      ))}
    </Tabs>
  );
}

export function DiscountCodeRedemptions() {
  const { selectedStoreIds } = useSelectedStores();
  const { newVsReturning } = useNewVsReturningState();
  const { data, isFetching } = useDiscountCodesAll({
    enabled: selectedStoreIds.length > 0,
    limit: 1,
    offset: 0,
    newVsReturning,
  });

  if (!selectedStoreIds.length) {
    return null;
  }

  if (isFetching && !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="h3">Discount Code Redemptions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative flex w-full justify-center items-center py-8">
            <Spinner className="mr-2" size="large" />
            <p className="text-foreground">Loading your data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.totalItemCount === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="h3">Discount Code Redemptions</CardTitle>
      </CardHeader>
      <CardContent className="overflow-visible">
        <p className="mb-2 text-sm text-muted-foreground">
          Performance metrics for all orders where a discount code was redeemed.
        </p>
        <DiscountCodesAllWithStoreTabs />
      </CardContent>
    </Card>
  );
}
